import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Receipt, Calendar, Plus, Trash, FilePdf, CheckCircle, X, FloppyDisk } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { BillsTable } from './billing/BillsTable';
import { logError, logWarn } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtInr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const todayMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const todayIso = () => new Date().toISOString().split('T')[0];

const Billing = () => {
  const [month, setMonth] = useState(todayMonth());
  const [preview, setPreview] = useState({ companies: [] });
  const [bills, setBills] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailConfigured, setEmailConfigured] = useState(false);

  // Edit dialog state
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null); // working copy

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, bRes, cRes, dRes] = await Promise.all([
        axios.get(`${API}/bills/preview`, { params: { month } }),
        axios.get(`${API}/bills`, { params: { month } }),
        axios.get(`${API}/candidates`),
        axios.get(`${API}/companies`),
      ]);
      setPreview(pRes.data);
      setBills(bRes.data);
      setAllCandidates(cRes.data || []);
      setDirectory(dRes.data || []);
    } catch (e) {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const monthLabel = useMemo(() => {
    try { return format(parseISO(`${month}-01`), 'MMMM yyyy'); } catch { return month; }
  }, [month]);

  const openEditor = async (companyPreview) => {
    // Get the next sequential bill number from backend
    let suggestedNumber = '';
    try {
      const { data } = await axios.get(`${API}/bills/next-number`);
      suggestedNumber = data.bill_number;
    } catch (e) {
      logWarn('Failed to fetch next bill number:', e?.message);
    }
    setDraft({
      company_name: companyPreview.company_name,
      month: companyPreview.month,
      bill_number: suggestedNumber,
      bill_date: todayIso(),
      company_contact: companyPreview.suggested_phone || companyPreview.suggested_contact || '',
      company_address: companyPreview.suggested_address || '',
      company_gstin: companyPreview.suggested_gstin || '',
      from_directory: companyPreview.from_directory || false,
      directory_company_id: companyPreview.directory_company_id || '',
      directory_company_name: companyPreview.directory_company_name || '',
      // Each line item gets a 'selected' flag (default true) so owner can toggle which ones go into the bill
      line_items: companyPreview.line_items.map(li => ({ ...li, selected: true })),
      notes: '',
      gst_percent: 0,
    });
    setOpen(true);
  };

  const applyDirectoryCompany = (companyId) => {
    const c = directory.find(x => x.id === companyId);
    if (!c) return;
    setDraft(d => ({
      ...d,
      company_contact: c.contact_phone || c.contact_email || '',
      company_address: (c.address || '').replace(/\s*\n+\s*/g, ', '),
      company_gstin: (c.gstin || '').toUpperCase(),
      from_directory: true,
      directory_company_id: c.id,
      directory_company_name: c.name,
    }));
    toast.success(`Details filled from ${c.name}`);
  };

  const updateLineItem = (idx, field, value) => {
    setDraft(d => ({
      ...d,
      line_items: d.line_items.map((li, i) => i === idx ? { ...li, [field]: value } : li),
    }));
  };

  const removeLineItem = (idx) => {
    setDraft(d => ({ ...d, line_items: d.line_items.filter((_, i) => i !== idx) }));
  };

  const addCustomLineItem = () => {
    setDraft(d => ({
      ...d,
      line_items: [...d.line_items, { candidate_id: null, candidate_name: '', position: '', joining_date: '', charge: 0, selected: true }],
    }));
  };

  const addCandidateById = (candidateId) => {
    if (!candidateId) return;
    const c = allCandidates.find(x => x.id === candidateId);
    if (!c) { toast.error('Candidate not found'); return; }
    setDraft(d => {
      if (d.line_items.some(li => li.candidate_id === c.id)) {
        toast.info(`${c.candidate_name} is already in this bill`);
        return d;
      }
      const charge = (c.charge_override != null ? c.charge_override : c.consultancy_charge) || 0;
      return {
        ...d,
        line_items: [
          ...d.line_items,
          {
            candidate_id: c.id,
            candidate_name: c.candidate_name,
            position: c.job_category || '',
            joining_date: c.date_of_joining || '',
            charge,
            selected: true,
          },
        ],
      };
    });
    toast.success(`Added ${c.candidate_name}`);
  };

  // Joined candidates for the draft's company that are not already in the line items
  const availableCandidates = useMemo(() => {
    if (!draft?.company_name) return [];
    const inBill = new Set(draft.line_items.map(li => li.candidate_id).filter(Boolean));
    return allCandidates
      .filter(c =>
        c.joining_status === 'Joined' &&
        (c.company_name || '').trim().toLowerCase() === draft.company_name.trim().toLowerCase() &&
        !inBill.has(c.id),
      )
      .sort((a, b) => (b.date_of_joining || '').localeCompare(a.date_of_joining || ''));
  }, [allCandidates, draft?.company_name, draft?.line_items]);

  const toggleSelectAll = (checked) => {
    setDraft(d => ({ ...d, line_items: d.line_items.map(li => ({ ...li, selected: checked })) }));
  };

  const totals = useMemo(() => {
    if (!draft) return { subtotal: 0, gst: 0, total: 0, selectedCount: 0 };
    const selected = draft.line_items.filter(li => li.selected);
    const subtotal = selected.reduce((s, li) => s + (parseFloat(li.charge) || 0), 0);
    const gst = subtotal * (parseFloat(draft.gst_percent) || 0) / 100;
    return { subtotal, gst, total: subtotal + gst, selectedCount: selected.length };
  }, [draft]);

  const handleSaveToDirectory = async () => {
    if (!draft?.company_name) return;
    const isEmail = (s) => /@/.test(s || '');
    const contact = (draft.company_contact || '').trim();
    const body = {
      name: draft.company_name.trim(),
      contact_phone: !isEmail(contact) ? contact : '',
      contact_email: isEmail(contact) ? contact : '',
      address: (draft.company_address || '').trim(),
      gstin: (draft.company_gstin || '').trim().toUpperCase(),
      notes: '',
    };
    try {
      await axios.post(`${API}/companies`, body);
      toast.success(`Saved “${body.name}” to directory`);
      setDraft(d => ({ ...d, from_directory: true }));
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to save to directory';
      // If it already exists, treat as success — they just want it on file.
      if (typeof detail === 'string' && /already exists/i.test(detail)) {
        toast.success(`“${body.name}” already in directory`);
        setDraft(d => ({ ...d, from_directory: true }));
      } else {
        toast.error(detail);
      }
    }
  };

  const handleSave = async () => {
    const selected = draft.line_items.filter(li => li.selected);
    if (!selected.length) { toast.error('Select at least one candidate to include in the bill'); return; }
    try {
      const { from_directory: _fd, directory_company_id: _dc, directory_company_name: _dn, ...draftBody } = draft;
      const payload = {
        ...draftBody,
        line_items: selected.map(li => {
          // Drop the UI-only 'selected' flag before sending to backend
          const { selected: _s, ...rest } = li;
          return { ...rest, charge: parseFloat(rest.charge) || 0 };
        }),
        gst_percent: parseFloat(draft.gst_percent) || 0,
      };
      const { data: newBill } = await axios.post(`${API}/bills`, payload);
      toast.success(`Bill ${newBill.bill_number} generated`);
      setOpen(false);
      setDraft(null);
      fetchAll();
      downloadPdf(newBill);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create bill');
    }
  };

  const previewPdf = async (bill) => {
    try {
      const res = await axios.get(`${API}/bills/${bill.id}.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) toast.error('Pop-up blocked — allow pop-ups to preview');
      else toast.success(`Preview opened: ${bill.bill_number}`);
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('Failed to preview PDF');
    }
  };

  const downloadPdf = async (bill) => {
    try {
      const res = await axios.get(`${API}/bills/${bill.id}.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `TulsiJobs_${bill.bill_number}_${bill.company_name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const deleteBill = async (bill) => {
    if (!window.confirm(`Delete bill ${bill.bill_number}? You'll be able to re-generate it.`)) return;
    try {
      await axios.delete(`${API}/bills/${bill.id}`);
      toast.success('Bill deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete bill');
    }
  };

  const togglePaid = async (bill) => {
    const next = !bill.paid;
    if (next && !window.confirm(`Mark bill ${bill.bill_number} as PAID?`)) return;
    if (!next && !window.confirm(`Un-mark bill ${bill.bill_number} as paid (revert to unpaid)?`)) return;
    try {
      await axios.patch(`${API}/bills/${bill.id}/paid`, { paid: next });
      toast.success(next ? `Bill ${bill.bill_number} marked as PAID` : `Bill ${bill.bill_number} reverted to UNPAID`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update bill');
    }
  };

  // Probe whether email is configured (controls visibility/state of the Email button)
  useEffect(() => {
    axios.get(`${API}/email/status`)
      .then(r => setEmailConfigured(!!r.data?.configured))
      .catch(() => setEmailConfigured(false));
  }, []);

  const emailBill = async (bill) => {
    if (!emailConfigured) {
      toast.error('Email not configured. Ask the admin to set RESEND_API_KEY in backend .env.');
      return;
    }
    const defaultTo = bill.company_contact && bill.company_contact.includes('@') ? bill.company_contact : '';
    const to = window.prompt(`Email bill ${bill.bill_number} to:`, defaultTo);
    if (!to) return;
    if (!to.includes('@')) { toast.error('Enter a valid email address'); return; }
    try {
      const { data } = await axios.post(`${API}/bills/${bill.id}/email`, { to });
      toast.success(`Email sent to ${data.to}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    }
  };

  const pendingCompanies = preview.companies.filter(c => !c.already_billed);

  return (
    <div data-testid="billing-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Billing</h1>
          <p className="text-base font-medium text-white">Generate monthly invoices for companies — one PDF per company per month</p>
        </div>
        <div className="flex items-center gap-3">
          <Calendar size={24} weight="duotone" className="text-[#A3E635]" />
          <Input data-testid="billing-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="bg-white border-2 border-[#A3E635] rounded-md px-3 py-2 text-black font-bold" />
        </div>
      </div>

      {loading ? <div className="text-white font-bold">Loading…</div> : (
        <>
          {/* Pending bills */}
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
            <Receipt size={24} weight="duotone" className="text-[#FDE047]" />
            Pending Bills — {monthLabel}
          </h2>
          <div className="bg-[#0F3622] border-2 border-[#FDE047] rounded-md shadow-[4px_4px_0px_0px_rgba(253,224,71,0.3)] overflow-x-auto mb-8">
            <table className="w-full tracker-table" data-testid="pending-bills-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Candidates</th>
                  <th>Subtotal</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingCompanies.length === 0 ? (
                  <tr><td colSpan="4" className="text-center py-6 text-white">All companies billed for {monthLabel}. 🎉</td></tr>
                ) : (
                  pendingCompanies.map((c) => (
                    <tr key={c.company_name} data-testid={`pending-bill-${c.company_name}`}>
                      <td className="font-bold">{c.company_name}</td>
                      <td>{c.line_items.length}</td>
                      <td className="font-bold">{fmtInr(c.subtotal)}</td>
                      <td>
                        <Button data-testid={`create-bill-${c.company_name}`} onClick={() => openEditor(c)}
                          className="bg-[#FDE047] text-black border-2 border-black rounded-md font-bold px-3 py-1 text-xs shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] transition-all">
                          <Plus size={14} weight="bold" className="mr-1" />
                          Create Bill
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Generated bills */}
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
            <CheckCircle size={24} weight="duotone" className="text-[#A3E635]" />
            Generated Bills — {monthLabel}
          </h2>
          <div className="bg-[#0F3622] border-2 border-[#A3E635] rounded-md shadow-[4px_4px_0px_0px_rgba(163,230,53,0.3)] overflow-x-auto">
            <BillsTable
              bills={bills}
              monthLabel={monthLabel}
              emailConfigured={emailConfigured}
              onPreviewPdf={previewPdf}
              onDownloadPdf={downloadPdf}
              onEmail={emailBill}
              onTogglePaid={togglePaid}
              onDelete={deleteBill}
            />
          </div>
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setDraft(null); }}>
        <DialogContent className="bg-[#0F3622] border-2 border-[#FDE047] shadow-[6px_6px_0px_0px_rgba(253,224,71,0.4)] sm:max-w-[820px] max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
              <Receipt size={28} weight="duotone" className="text-[#FDE047]" />
              Create Bill — {draft?.company_name}
            </DialogTitle>
            <DialogDescription className="sr-only">Dialog content</DialogDescription>
          </DialogHeader>

          {draft && (
            <div className="space-y-3 mt-2">
              <div className="bg-[#143E29] border-2 border-[#A3E635] rounded-md px-3 py-2 flex flex-wrap items-center gap-3" data-testid="directory-picker-row">
                <Label className="text-xs font-bold uppercase tracking-wider text-white whitespace-nowrap">Party details from Companies directory</Label>
                <select
                  data-testid="bill-directory-select"
                  value={draft.directory_company_id || ''}
                  onChange={(e) => applyDirectoryCompany(e.target.value)}
                  className="flex-1 min-w-[220px] bg-white border-2 border-black rounded-md px-3 py-1.5 text-black font-bold text-sm"
                >
                  <option value="">— Pick a company to auto-fill —</option>
                  {directory.map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.sla_generated_at ? ' · SLA ✓' : ''}</option>
                  ))}
                </select>
              </div>
              {draft.from_directory && (
                <div
                  data-testid="from-directory-hint"
                  className="bg-[#A3E635] border-2 border-black rounded-md px-3 py-2 text-xs font-bold text-black flex items-center gap-2"
                >
                  Contact, address & GSTIN auto-filled from <span className="underline">{draft.directory_company_name || 'the Companies directory'}</span> ✓ Edit any field below to override for this bill only.
                </div>
              )}
              {!draft.from_directory && (draft.company_contact || draft.company_address || draft.company_gstin) && (
                <div
                  data-testid="save-to-directory-prompt"
                  className="bg-[#143E29] border-2 border-[#FDE047] rounded-md px-3 py-2 text-xs font-bold text-white flex items-center justify-between gap-3"
                >
                  <span>
                    Not in directory yet — save these details so next bill for <span className="text-[#FDE047]">{draft.company_name}</span> auto-fills.
                  </span>
                  <Button
                    type="button"
                    data-testid="save-to-directory-btn"
                    onClick={handleSaveToDirectory}
                    className="bg-[#FDE047] text-black border-2 border-black rounded-md px-3 py-1.5 text-xs font-bold hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all whitespace-nowrap"
                  >
                    + Save to Directory
                  </Button>
                </div>
              )}
              {/* Bill Meta */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-white">Bill Number</Label>
                  <Input data-testid="bill-number-input" placeholder="TJ-YYMM-NNNN" value={draft.bill_number}
                    onChange={(e) => setDraft({...draft, bill_number: e.target.value})}
                    className="bg-white border-2 border-[#FDE047] rounded-md px-3 py-2 text-black font-bold" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-white">Bill Date</Label>
                  <Input data-testid="bill-date" type="date" value={draft.bill_date}
                    onChange={(e) => setDraft({...draft, bill_date: e.target.value})}
                    className="bg-white border-2 border-[#FDE047] rounded-md px-3 py-2 text-black" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-white">Party Contact Number</Label>
                  <Input data-testid="bill-company-contact" placeholder="+91-9876543210" value={draft.company_contact}
                    onChange={(e) => setDraft({...draft, company_contact: e.target.value})}
                    className="bg-white border-2 border-[#FDE047] rounded-md px-3 py-2 text-black" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-white">Party Address (optional)</Label>
                <Input data-testid="bill-company-address" placeholder="123 Tech Park, Bangalore-560001" value={draft.company_address}
                  onChange={(e) => setDraft({...draft, company_address: e.target.value})}
                  className="bg-white border-2 border-[#FDE047] rounded-md px-3 py-2 text-black" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-white">
                  Party GSTIN (optional) <span className="text-[10px] text-[#FDE047] normal-case">— shown on PDF for GST input-credit</span>
                </Label>
                <Input
                  data-testid="bill-company-gstin"
                  placeholder="19ABCDE1234F1Z5"
                  value={draft.company_gstin || ''}
                  onChange={(e) => setDraft({...draft, company_gstin: e.target.value.toUpperCase()})}
                  className="bg-white border-2 border-[#FDE047] rounded-md px-3 py-2 text-black uppercase"
                  maxLength={15}
                />
              </div>

              {/* Line items */}
              <div className="border-t-2 border-[#143E29] pt-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-white">
                    Line Items <span className="ml-2 text-[#FDE047]">({totals.selectedCount} of {draft.line_items.length} selected)</span>
                  </Label>
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-white flex items-center gap-1.5 cursor-pointer">
                      <input
                        data-testid="bill-select-all"
                        type="checkbox"
                        checked={draft.line_items.length > 0 && draft.line_items.every(li => li.selected)}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        className="w-4 h-4 accent-[#A3E635]"
                      />
                      Select all
                    </label>
                    <button data-testid="add-custom-line-item" type="button" onClick={addCustomLineItem}
                      className="text-xs font-bold text-[#FDE047] underline hover:text-white">+ Add custom item</button>
                  </div>
                </div>

                {availableCandidates.length > 0 && (
                  <div className="mb-3 bg-[#0B2818] border-2 border-[#93C5FD] rounded-md px-3 py-2 flex items-center gap-3">
                    <span className="text-xs font-bold text-white whitespace-nowrap">
                      Add joined candidate from {draft.company_name}:
                    </span>
                    <select
                      data-testid="add-candidate-picker"
                      value=""
                      onChange={(e) => { addCandidateById(e.target.value); e.target.value = ''; }}
                      className="flex-1 bg-white text-[#0A0A0A] border-2 border-black rounded-md px-2 py-1.5 text-xs font-bold cursor-pointer [&>option]:bg-white [&>option]:text-[#0A0A0A]"
                    >
                      <option value="">— Pick a candidate ({availableCandidates.length} available) —</option>
                      {availableCandidates.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.candidate_name}
                          {c.job_category ? ` · ${c.job_category}` : ''}
                          {c.date_of_joining ? ` · joined ${c.date_of_joining}` : ''}
                          {` · ₹${(c.charge_override != null ? c.charge_override : c.consultancy_charge) || 0}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                  {draft.line_items.map((li, idx) => (
                    <div key={li.candidate_id || `custom-${idx}`} className={`bg-[#0B2818] border-2 rounded-md p-2 grid grid-cols-12 gap-2 items-center transition-opacity ${li.selected ? 'border-[#A3E635]' : 'border-[#143E29] opacity-50'}`}>
                      <div className="col-span-1 flex justify-center">
                        <input
                          data-testid={`li-select-${idx}`}
                          type="checkbox"
                          checked={!!li.selected}
                          onChange={(e) => updateLineItem(idx, 'selected', e.target.checked)}
                          className="w-4 h-4 accent-[#A3E635] cursor-pointer"
                        />
                      </div>
                      <div className="col-span-3">
                        <Input data-testid={`li-name-${idx}`} placeholder="Candidate name" value={li.candidate_name}
                          onChange={(e) => updateLineItem(idx, 'candidate_name', e.target.value)}
                          className="bg-white border border-[#143E29] rounded px-2 py-1 text-sm text-black" />
                      </div>
                      <div className="col-span-3">
                        <Input data-testid={`li-pos-${idx}`} placeholder="Position (auto from job category)" value={li.position || ''}
                          onChange={(e) => updateLineItem(idx, 'position', e.target.value)}
                          className="bg-white border border-[#143E29] rounded px-2 py-1 text-sm text-black" />
                      </div>
                      <div className="col-span-2">
                        <Input data-testid={`li-date-${idx}`} type="date" value={li.joining_date || ''}
                          onChange={(e) => updateLineItem(idx, 'joining_date', e.target.value)}
                          className="bg-white border border-[#143E29] rounded px-2 py-1 text-sm text-black" />
                      </div>
                      <div className="col-span-2">
                        <Input data-testid={`li-charge-${idx}`} type="number" min="0" step="100" placeholder="Charge" value={li.charge}
                          onChange={(e) => updateLineItem(idx, 'charge', e.target.value)}
                          className="bg-white border border-[#143E29] rounded px-2 py-1 text-sm text-black text-right font-bold" />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button data-testid={`li-del-${idx}`} type="button" onClick={() => removeLineItem(idx)} title="Remove"
                          className="p-1 bg-[#F87171] text-white border-2 border-black rounded hover:-translate-y-0.5 transition-all">
                          <X size={12} weight="bold" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* GST + Notes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t-2 border-[#143E29] pt-3">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-white">GST % (optional)</Label>
                  <Input data-testid="bill-gst" type="number" min="0" max="50" step="0.5" value={draft.gst_percent}
                    onChange={(e) => setDraft({...draft, gst_percent: e.target.value})}
                    className="bg-white border-2 border-[#FDE047] rounded-md px-3 py-2 text-black" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-white">Notes (optional)</Label>
                  <Input data-testid="bill-notes" placeholder="Thank you for your business!" value={draft.notes}
                    onChange={(e) => setDraft({...draft, notes: e.target.value})}
                    className="bg-white border-2 border-[#FDE047] rounded-md px-3 py-2 text-black" />
                </div>
              </div>

              {/* Live totals */}
              <div className="bg-[#0B2818] border-2 border-[#A3E635] rounded-md p-3 space-y-1 text-right">
                <div className="text-sm text-white">Subtotal: <span className="font-bold ml-2">{fmtInr(totals.subtotal)}</span></div>
                {totals.gst > 0 && <div className="text-sm text-white">GST: <span className="font-bold ml-2">{fmtInr(totals.gst)}</span></div>}
                <div className="text-xl font-bold text-[#A3E635]">Total: {fmtInr(totals.total)}</div>
              </div>

              <Button data-testid="save-bill" onClick={handleSave}
                className="w-full bg-[#A3E635] text-black border-2 border-[#A3E635] rounded-md font-bold px-4 py-3 shadow-[3px_3px_0px_0px_rgba(163,230,53,0.5)] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_rgba(163,230,53,0.7)] transition-all">
                <FloppyDisk size={20} weight="bold" className="mr-2" />
                Save &amp; Download PDF
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Billing;
